import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";

import Popover, { MenuItem } from "./Popover";
import { figureDir, formatCell, formatDenominator } from "./reportFormat";
import { labelFor } from "../../services/enumLabels";

const storageKey = (userId, reportKey) => `pl.reports.columns.${userId ?? "me"}.${reportKey}`;

const readHidden = (userId, reportKey) => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId, reportKey));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeHidden = (userId, reportKey, visible) => {
  try {
    window.localStorage.setItem(storageKey(userId, reportKey), JSON.stringify(visible));
  } catch {
    // Storage blocked: the choice simply lasts for this page view.
  }
};

/**
 * Columns shown, remembered per user per report (spec 02). `currency` is only
 * worth a column when the table mixes currencies ("All currencies").
 */
function useVisibleColumns(columns, reportKey, userId, multiCurrency) {
  const defaults = useMemo(
    () =>
      columns.filter((c) => c.default_visible && (c.key !== "currency" || multiCurrency)).map((c) => c.key),
    [columns, multiCurrency]
  );

  const [saved, setSaved] = useState(() => readHidden(userId, reportKey));

  useEffect(() => {
    setSaved(readHidden(userId, reportKey));
  }, [userId, reportKey]);

  const visible = saved ? columns.filter((c) => saved.includes(c.key)).map((c) => c.key) : defaults;

  const toggle = (key) => {
    const next = visible.includes(key) ? visible.filter((k) => k !== key) : [...visible, key];
    // Never zero columns — a table with nothing in it reads as broken.
    if (next.length === 0) return;
    writeHidden(userId, reportKey, next);
    setSaved(next);
  };

  const reset = () => {
    try {
      window.localStorage.removeItem(storageKey(userId, reportKey));
    } catch {
      // ignore
    }
    setSaved(null);
  };

  return { visible, toggle, reset };
}

/**
 * Whether the table is wider than its card. A table that fits lays out with no
 * scroll box of its own, so its header can stick under the topbar while the page
 * scrolls; a wider one scrolls sideways in its own box (which, being a scroll
 * container, is what the header would then stick to — so it doesn't).
 */
function useOverflowsX(boxRef, tableRef, ready) {
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const table = tableRef.current;
    if (!box || !table || typeof ResizeObserver === "undefined") return undefined;

    const measure = () => setOverflows(table.scrollWidth > box.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(table);
    measure();
    return () => observer.disconnect();
  }, [boxRef, tableRef, ready]);

  return overflows;
}

/**
 * The results table (spec 02 §4): group-by, column picker, sticky header,
 * server-side sorting and paging, bold totals (one per currency), a chevron to
 * the drilldown, and the footnote with the formula.
 */
const TONES = {
  danger: "border-danger-line bg-danger-wash text-danger-text",
  warning: "border-warning-line bg-warning-wash text-warning-text",
  success: "border-accent-line bg-accent-wash text-accent-text",
};

export default function ReportTable({
  reportKey,
  userId,
  table,
  multiCurrency,
  groupBy,
  onGroupBy,
  sort,
  direction,
  onSort,
  onPage,
  onRow,
  notes = [],
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const columns = table?.columns ?? [];
  const rows = table?.rows ?? [];
  const totals = table?.totals ?? [];
  const pagination = table?.pagination ?? { current_page: 1, last_page: 1, total: rows.length };
  const groupOptions = table?.group_by_options ?? [];

  const { visible, toggle, reset } = useVisibleColumns(columns, reportKey, userId, multiCurrency);
  const shown = columns.filter((c) => visible.includes(c.key));
  const drillable = Boolean(onRow) && rows.some((r) => r.point);
  const boxRef = useRef(null);
  const tableRef = useRef(null);
  const overflows = useOverflowsX(boxRef, tableRef, rows.length > 0);

  // What the drilldown drawer calls this row: its first column as shown ("Cash", "1 Sep").
  const rowLabel = (row) => (columns[0] ? formatCell(row[columns[0].key], columns[0].type, locale, t) : "");

  const cell = (row, column) => {
    const value = row[column.key];
    const text = formatCell(value, column.type, locale, t);
    const denominator = column.type === "percent" ? formatDenominator(value, locale, t) : null;
    // A state cell coloured like the rest of the portal (low / out stock, ✓ / ✗).
    const tone = TONES[row.tones?.[column.key]];

    if (tone) {
      return (
        <span className={`inline-block rounded-full border px-2 py-0.5 text-caption font-semibold ${tone}`}>
          {text}
        </span>
      );
    }

    return (
      <>
        {/* Figures stay LTR ("JOD 3,240.00", "52.9%"); anything with words
            ("28 سبتمبر 2026", "1 س 00 د", "غير متاح") follows its own direction,
            isolated so it never reorders what sits next to it. */}
        <bdi dir={column.type === "text" ? "auto" : figureDir(text)}>{text}</bdi>
        {denominator && <span className="ms-1 text-caption text-ink-2">· {denominator}</span>}
      </>
    );
  };

  const header = (column) => {
    const active = sort === column.key;
    const Icon = active ? (direction === "desc" ? ArrowDownIcon : ArrowUpIcon) : ChevronUpDownIcon;

    if (!column.sortable) return column.label;

    return (
      <button
        type="button"
        onClick={() => onSort(column.key, active && direction === "asc" ? "desc" : "asc")}
        className={`inline-flex items-center gap-1 ${column.align === "end" ? "flex-row-reverse" : ""} hover:text-ink`}
        title={column.hint ?? undefined}
      >
        <span>{column.label}</span>
        <Icon className={`h-3.5 w-3.5 ${active ? "text-ink" : "text-ink-muted"}`} aria-hidden="true" />
      </button>
    );
  };

  const align = (column) => (column.align === "end" ? "text-end" : "text-start");

  return (
    <div className="card min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
        <p className="text-card-title font-bold">{t("reports.shell.results")}</p>

        <div className="flex flex-wrap items-center gap-2">
          {groupOptions.length > 1 && (
            <Popover
              label={t("reports.shell.groupByValue", {
                value: labelFor("report_group", groupBy),
              })}
              align="end"
              panelClassName="w-48"
            >
              {(close) =>
                groupOptions.map((option) => (
                  <MenuItem
                    key={option}
                    selected={groupBy === option}
                    onClick={() => {
                      onGroupBy(option);
                      close();
                    }}
                  >
                    {labelFor("report_group", option)}
                  </MenuItem>
                ))
              }
            </Popover>
          )}

          <Popover icon={TableCellsIcon} label={t("reports.shell.columns")} align="end" panelClassName="w-64">
            <div className="max-h-80 overflow-y-auto">
              {columns.map((column) => (
                <label
                  key={column.key}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-dense hover:bg-raised"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--pl-accent)]"
                    checked={visible.includes(column.key)}
                    onChange={() => toggle(column.key)}
                  />
                  <span className="truncate">{column.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-1 border-t border-line pt-1">
              <MenuItem onClick={reset}>{t("reports.shell.resetColumns")}</MenuItem>
            </div>
          </Popover>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-12 text-center text-dense text-ink-2">{t("reports.shell.noData")}</p>
      ) : (
        // No fixed height: the table grows with its rows and the page scrolls.
        <div ref={boxRef} className={`mt-3 ${overflows ? "overflow-x-auto" : ""}`}>
          <table ref={tableRef} className="w-full min-w-max border-collapse text-dense">
            <thead
              className={`bg-raised text-ink-2 ${overflows ? "" : "sticky top-[var(--pl-topbar-h,0px)] z-10"}`}
            >
              <tr>
                {shown.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`whitespace-nowrap px-4 py-2.5 font-semibold ${align(column)}`}
                  >
                    {header(column)}
                  </th>
                ))}
                {drillable && <th aria-hidden="true" className="w-10" />}
              </tr>
            </thead>

            <tbody className="divide-y divide-line">
              {rows.map((row, index) => (
                <tr
                  key={`${row.point ?? index}-${row.currency ?? ""}`}
                  className={drillable && row.point ? "cursor-pointer hover:bg-raised" : ""}
                  onClick={drillable && row.point ? () => onRow(row.point, rowLabel(row)) : undefined}
                >
                  {shown.map((column) => (
                    <td
                      key={column.key}
                      className={`whitespace-nowrap px-4 py-2.5 tabular-nums ${align(column)}`}
                    >
                      {cell(row, column)}
                    </td>
                  ))}
                  {drillable && (
                    <td className="px-2 text-ink-2">
                      {row.point && (
                        <button
                          type="button"
                          aria-label={t("reports.shell.openDetail")}
                          onClick={(event) => {
                            event.stopPropagation();
                            onRow(row.point, rowLabel(row));
                          }}
                          className="grid h-8 w-8 place-items-center rounded-full hover:bg-surface"
                        >
                          <ChevronRightIcon className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>

            {totals.length > 0 && (
              // An ordinary last row (it used to stick to the scroll box's bottom and cover the last data row).
              <tfoot className="bg-raised font-bold">
                {totals.map((row, index) => (
                  <tr key={`total-${row.currency ?? index}`} className="border-t-2 border-line-strong">
                    {shown.map((column, columnIndex) => (
                      <td
                        key={column.key}
                        className={`whitespace-nowrap px-4 py-3 tabular-nums ${align(column)}`}
                      >
                        {columnIndex === 0 && column.type === "text" && !row[column.key]
                          ? t("reports.shell.total")
                          : cell(row, column)}
                      </td>
                    ))}
                    {drillable && <td />}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
      )}

      {(table?.footnote || notes.length > 0) && (
        <div className="space-y-1.5 border-t border-line px-4 pb-1 pt-3 text-caption leading-5 text-ink-2">
          {table?.footnote && <p className="break-words">{table.footnote}</p>}
          {notes.map((note) => (
            <p key={note} className="break-words text-warning-text">
              {note}
            </p>
          ))}
        </div>
      )}

      <div
        className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 pb-4 pt-3 text-dense text-ink-2 ${
          table?.footnote || notes.length > 0 ? "" : "border-t border-line"
        }`}
      >
        <span>{t("reports.shell.resultsCount", { count: pagination.total })}</span>

        {pagination.last_page > 1 && (
          <Pager page={pagination.current_page} last={pagination.last_page} onPage={onPage} />
        )}
      </div>
    </div>
  );
}

function Pager({ page, last, onPage }) {
  const { t } = useTranslation();

  const pages = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(last, page + 2); p += 1) pages.push(p);

  const button = "grid h-8 min-w-8 place-items-center rounded-lg border border-line px-2";

  return (
    <nav className="flex items-center gap-1" aria-label={t("reports.shell.pagination")}>
      <button
        type="button"
        className={`${button} disabled:opacity-40`}
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        aria-label={t("reports.shell.previousPage")}
      >
        <ChevronLeftIcon className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPage(p)}
          aria-current={p === page ? "page" : undefined}
          className={`${button} ${p === page ? "border-accent bg-accent font-bold text-accent-on" : "hover:bg-raised"}`}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        className={`${button} disabled:opacity-40`}
        disabled={page >= last}
        onClick={() => onPage(page + 1)}
        aria-label={t("reports.shell.nextPage")}
      >
        <ChevronRightIcon className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
      </button>
    </nav>
  );
}
